import { BaseModal } from "@/components/modal/base-modal";
import { SegmentedControl } from "@/components/toggle";
import { SwapPickItem } from "@/features/meal-plan/components/swap-pick-item";
import { useGetCarbSwaps } from "@/hooks/requests/meal/use-get-carb-swaps";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonSkeletonText } from "@ionic/react";
import { memo, useMemo, useState } from "react";

interface CarbSwapsBottomSheetProps {
  isOpen: boolean;
  onDismiss: () => void;
}

type WeightType = "uncooked" | "cooked";

const weightTypeOptions: { value: WeightType; label: string }[] = [
  { value: "uncooked", label: "Uncooked Weight" },
  { value: "cooked", label: "Cooked Weight" },
];

export const CarbSwapsBottomSheet = memo(function CarbSwapsBottomSheet({
  isOpen,
  onDismiss,
}: CarbSwapsBottomSheetProps) {
  const [selectedWeightType, setSelectedWeightType] = useState<WeightType>("uncooked");
  const { carbSwaps, isLoading } = useGetCarbSwaps();

  const groupedSwaps = useMemo(() => {
    const options = carbSwaps?.carbSwaps ?? [];
    if (!carbSwaps?.carbSwaps) return new Map<string, typeof options>();

    const grouped = new Map<string, typeof options>();
    options.forEach((swap) => {
      const type = swap.type || "other";
      if (!grouped.has(type)) {
        grouped.set(type, []);
      }
      grouped.get(type)!.push(swap);
    });

    return grouped;
  }, [carbSwaps]);

  return (
    <BaseModal
      isOpen={isOpen}
      onDismiss={onDismiss}
      backdropDismiss={true}
      initialBreakpoint={0.55}
      breakpoints={[0, 0.55, 1]}
      handle={true}
    >
      <div className="flex flex-col gap-3 overflow-y-auto px-7 pt-6 pb-20">
        <h2 className={cn(textVariants.title3, "text-center")}>Carb Swaps</h2>

        <SegmentedControl
          options={weightTypeOptions}
          value={selectedWeightType}
          onChange={(val: string) => setSelectedWeightType(val as WeightType)}
        />

        {isLoading ? (
          <div className="flex flex-wrap justify-between gap-2">
            {[...Array(6)].map((_, index) => (
              <IonSkeletonText key={index} animated className="h-48 w-full rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {Array.from(groupedSwaps.entries()).map(([type, swaps]) => (
              <div key={type} className="flex flex-col gap-3">
                <h3 className={cn(textVariants.title3, "capitalize")}>{type}</h3>
                <div className="grid grid-cols-3 gap-2">
                  {swaps.map((swap, index) => {
                    const description =
                      selectedWeightType === "uncooked"
                        ? `${swap.weightUncookedGrams}g`
                        : `${swap.weightCookedGrams}g`;
                    return (
                      <SwapPickItem
                        key={`${type}-${index}`}
                        imageUrl={swap.imageUrl}
                        name={swap.name}
                        description={description}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </BaseModal>
  );
});
